// CB:BEGIN admin-add-contractor
import React, { useState } from 'react';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import SettingsCard from '../../components/ui/SettingsCard';
import { useApi } from '@/lib/useApi';

interface AdminAddContractorProps {
  onNavigate?: (screen: string) => void;
}

const AdminAddContractor: React.FC<AdminAddContractorProps> = ({ onNavigate }) => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Contractor Information
  const [company, setCompany] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');

  // Business Information
  const [licenseNumber, setLicenseNumber] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [yearsInBusiness, setYearsInBusiness] = useState('');
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [insurancePolicyNumber, setInsurancePolicyNumber] = useState('');
  const [insuranceExpiry, setInsuranceExpiry] = useState('');

  // Service Areas
  const [serviceAreas, setServiceAreas] = useState<string[]>([]);
  const [specialties, setSpecialties] = useState<string[]>([]);

  // Clerk Account Information
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Verification Status
  const [verified, setVerified] = useState(false);

  const serviceAreaOptions = [
    'Roofing', 'Gutters', 'Siding', 'Windows', 'Exterior Painting', 
    'Deck Building', 'Fencing', 'HVAC', 'Plumbing', 'Electrical'
  ];

  const specialtyOptions = [
    'Residential', 'Commercial', 'Industrial', 'Historic Restoration',
    'Green Building', 'Emergency Repairs', 'Insurance Work', 'New Construction'
  ];

  const handleServiceAreaToggle = (area: string) => {
    setServiceAreas(prev => 
      prev.includes(area) 
        ? prev.filter(a => a !== area)
        : [...prev, area]
    );
  };

  const handleSpecialtyToggle = (specialty: string) => {
    setSpecialties(prev => 
      prev.includes(specialty) 
        ? prev.filter(s => s !== specialty)
        : [...prev, specialty]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!company || !firstName || !lastName || !email || !password) {
        throw new Error('Please fill in all required fields');
      }

      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      // Create contractor
      const contractorData = {
        // Contractor Information
        company,
        firstName,
        lastName,
        email,
        phone,
        businessAddress,
        city,
        state,
        zipCode,
        
        // Business Information
        licenseNumber,
        businessName: businessName || company,
        yearsInBusiness: yearsInBusiness ? parseInt(yearsInBusiness) : null,
        insuranceProvider,
        insurancePolicyNumber,
        insuranceExpiry: insuranceExpiry ? new Date(insuranceExpiry).toISOString() : null,
        
        // Service Information
        serviceAreas,
        specialties,
        
        // Clerk Account
        password,
        
        // Verification
        verified
      };

      const response = await api.post('/api/admin/contractors', contractorData);
      
      if (response.success) {
        setSuccess(true);
        // Reset form
        setCompany('');
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setBusinessAddress('');
        setCity('');
        setState('');
        setZipCode('');
        setLicenseNumber('');
        setBusinessName('');
        setYearsInBusiness('');
        setInsuranceProvider('');
        setInsurancePolicyNumber('');
        setInsuranceExpiry('');
        setServiceAreas([]);
        setSpecialties([]);
        setPassword('');
        setConfirmPassword('');
        setVerified(false);
      } else {
        throw new Error(response.error || 'Failed to create contractor');
      }
    } catch (err) {
      console.error('Error creating contractor:', err);
      setError(err instanceof Error ? err.message : 'Failed to create contractor');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <ResponsiveContainer className="container">
        <div className="content">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 16, color: '#059669' }}>✅</div>
            <h2 style={{ fontSize: 24, fontWeight: '600', color: '#111827', marginBottom: 8 }}>
              Contractor Created Successfully!
            </h2>
            <p style={{ color: '#6b7280', marginBottom: 24 }}>
              The contractor account has been created with Clerk authentication and business information.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={() => setSuccess(false)}
                className="btn btn-secondary"
                style={{ padding: '10px 20px' }}
              >
                Add Another Contractor
              </button>
              <button
                onClick={() => onNavigate?.('admin-contractors-list')}
                className="btn btn-primary"
                style={{ padding: '10px 20px' }}
              >
                Back to Contractors List
              </button>
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer className="container">
      <SharedHeaderAdmin onNavigate={onNavigate} />
      <div className="content">
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            <button 
              className="btn btn-secondary" 
              onClick={() => onNavigate?.('admin-contractors-list')}
              style={{ fontSize: 14, padding: '8px 16px' }}
            >
              ← Back to Contractors
            </button>
            <h1 style={{ fontSize: 28, margin: 0 }}>Add New Contractor</h1>
          </div>
          <p style={{ color: '#6b7280', marginTop: 8 }}>
            Create a new contractor account with Clerk authentication and business information
          </p>
        </div>

        {error && (
          <div style={{ 
            background: '#fee2e2', 
            border: '1px solid #fecaca', 
            borderRadius: 8, 
            padding: 16, 
            marginBottom: 24,
            color: '#dc2626'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Company Information */}
          <SettingsCard title="Company Information">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Company Name *
                </label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="form-control"
                  required
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Business Name (Legal)
                </label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                  placeholder="Leave blank to use company name"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Years in Business
                </label>
                <input
                  type="number"
                  value={yearsInBusiness}
                  onChange={(e) => setYearsInBusiness(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </SettingsCard>

          {/* Contact Information */}
          <SettingsCard title="Contact Information">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  First Name *
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="form-control"
                  required
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Last Name *
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="form-control"
                  required
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-control"
                  required
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </SettingsCard>

          {/* Business Address */}
          <SettingsCard title="Business Address">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Business Address
                </label>
                <input
                  type="text"
                  value={businessAddress}
                  onChange={(e) => setBusinessAddress(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  City
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  State
                </label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </SettingsCard>

          {/* Licensing & Insurance */}
          <SettingsCard title="Licensing & Insurance">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  License Number
                </label>
                <input
                  type="text"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Insurance Provider
                </label>
                <input
                  type="text"
                  value={insuranceProvider}
                  onChange={(e) => setInsuranceProvider(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Insurance Policy Number
                </label>
                <input
                  type="text"
                  value={insurancePolicyNumber}
                  onChange={(e) => setInsurancePolicyNumber(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Insurance Expiry Date
                </label>
                <input
                  type="date"
                  value={insuranceExpiry}
                  onChange={(e) => setInsuranceExpiry(e.target.value)}
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </SettingsCard>

          {/* Service Areas */}
          <SettingsCard title="Service Areas">
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 8 }}>
                Select Service Areas
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
                {serviceAreaOptions.map((area) => (
                  <label key={area} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={serviceAreas.includes(area)}
                      onChange={() => handleServiceAreaToggle(area)}
                    />
                    <span style={{ fontSize: 14, color: '#374151' }}>{area}</span>
                  </label>
                ))}
              </div>
            </div>
          </SettingsCard>

          {/* Specialties */}
          <SettingsCard title="Specialties">
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 8 }}>
                Select Specialties
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
                {specialtyOptions.map((specialty) => (
                  <label key={specialty} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={specialties.includes(specialty)}
                      onChange={() => handleSpecialtyToggle(specialty)}
                    />
                    <span style={{ fontSize: 14, color: '#374151' }}>{specialty}</span>
                  </label>
                ))}
              </div>
            </div>
          </SettingsCard>

          {/* Clerk Account Information */}
          <SettingsCard title="Clerk Account Information">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Password *
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-control"
                  required
                  style={{ width: '100%' }}
                  minLength={8}
                />
                <small style={{ color: '#6b7280', fontSize: 12 }}>
                  Minimum 8 characters
                </small>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
                  Confirm Password *
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="form-control"
                  required
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </SettingsCard>

          {/* Verification Status */}
          <SettingsCard title="Verification Status">
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={verified}
                  onChange={(e) => setVerified(e.target.checked)}
                />
                <span style={{ fontSize: 14, color: '#374151' }}>
                  Mark contractor as verified (skip verification process)
                </span>
              </label>
            </div>
          </SettingsCard>

          {/* Submit Button */}
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              style={{ 
                fontSize: 16, 
                padding: '12px 32px',
                background: '#059669',
                border: 'none',
                borderRadius: 8,
                color: 'white',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Creating Contractor...' : 'Create Contractor'}
            </button>
          </div>
        </form>
      </div>
    </ResponsiveContainer>
  );
};

export default AdminAddContractor;
// CB:END admin-add-contractor
